import React, { useEffect, useState } from "react";
import {
  Box, Button, MenuItem, Select, TextField, Typography,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, Dialog, DialogActions, DialogContent, DialogTitle
} from "@mui/material";
import { Add, Save } from "@mui/icons-material";
import { useSelector } from "react-redux";
import axiosInstance from '../../axiosInstance';

const ReportDesign = () => {
  const orgId = useSelector((state) =>
    state?.account?.org?.id ? state.account.org.id : state?.account?.org_data?.data?.id
  );

  const BASE_URL = "https://api.projectivetech.com";

  const [reportList, setReportList] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState("");
  const [rows, setRows] = useState([]);
  const [activities, setActivities] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountType, setSelectedAccountType] = useState({});
  const [isNewReport, setIsNewReport] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [reportName, setReportName] = useState("");
  const [reportType, setReportType] = useState("");

  useEffect(() => {
    axiosInstance.get(`${BASE_URL}/activity/list_reports_by_org/?org_id=${orgId}`).then((res) => setReportList(res.data));
    axiosInstance.get(`${BASE_URL}/activity/business_activity_master_list/?org_id=${orgId}`).then((res) => setActivities(res.data));
    axiosInstance.get(`${BASE_URL}/account/get_coa_by_org_id?org_id=${orgId}`)
      .then((res) => { setAccounts(res.data.data); })
      .catch((err) => console.error("COA error:", err));
  }, [orgId]);

  useEffect(() => {
    if (selectedReportId && !isNewReport) {
      axiosInstance.get(`${BASE_URL}/activity/fetch_report_rows/?report_id=${selectedReportId}`)
        .then((res) => setRows(res.data))
        .catch((err) => console.error("Error fetching report rows", err));
    }
  }, [selectedReportId, isNewReport]);

  const handleReportChange = (e) => {
    const val = e.target.value;
    if (val === "new") {
      setDialogOpen(true);
    } else {
      setSelectedReportId(val);
      setIsNewReport(false);
      setReportName("");
      setReportType("");
    }
  };

  const handleDialogSubmit = () => {
    axiosInstance.get(`${BASE_URL}/activity/generate_report_id/?org_id=${orgId}`).then((res) => {
      setSelectedReportId(res.data.report_id);
      setIsNewReport(true);
      setRows([]);
      setDialogOpen(false);
    });
  };

  const handleAddRow = () => handleInsertRow(rows.length);

  const handleInsertRow = (position) => {
    const newRow = {
      display_name: "",
      data_source: "",
      line_id: "",
      display_order: 0,
      activity_cd: "",
      account_type: "",
      account_code: "",
      formula: "",
      format: ""
    };

    const updatedRows = [...rows];
    updatedRows.splice(position, 0, newRow);

    updatedRows.forEach((row, i) => {
      row.display_order = i + 1;
      row.line_id = `T${String(i + 1).padStart(3, "0")}`;
    });

    setRows(updatedRows);
  };

  const handleCellChange = (index, field, value) => {
    const updated = [...rows];
    updated[index][field] = value;
    setRows(updated);
  };

  const handleAccountTypeChange = (index, value) => {
    const updated = [...rows];
    updated[index].account_type = value;
    updated[index].account_code = "";
    setSelectedAccountType({ ...selectedAccountType, [index]: value });
    setRows(updated);
  };

  const handleSave = () => {
    const payload = {
      org_id: orgId,
      report_id: selectedReportId,
      ...(reportName && { report_name: reportName }),
      ...(reportType && { report_type: reportType }),
      rows
    };

    axiosInstance.post(`${BASE_URL}/activity/save_report_template/`, payload)
      .then(() => {
        alert("Report saved!");
        setIsNewReport(false);
      })
      .catch(() => alert("Save failed"));
  };

  const accountTypeOptions = [...Array(10)].map((_, i) => `account_type${i + 1}`);

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>📊 Report Designer</Typography>

      <Select value={selectedReportId} onChange={handleReportChange} sx={{ mb: 2 }}>
        {reportList.map((r) => (
          <MenuItem key={r.report_id} value={r.report_id}>
            {r.report_name} ({r.report_type})
          </MenuItem>
        ))}
        <MenuItem value="new">➕ Create New Report</MenuItem>
      </Select>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#1976d2" }}>
            <TableRow>
              {["Display Order", "Line ID", "Display Name", "Data Source", "Activity", "Account Type", "Account Code", "Formula", "Format", ""].map((header) => (
                <TableCell key={header} sx={{ color: "white" }}>{header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const selectedTypeKey = row.account_type; // e.g., 'account_type1'
              const mappedKey = selectedTypeKey ? `Account Type ${selectedTypeKey.slice(-1)}` : null;
              
              const accountGroupOptions = mappedKey
                ? [...new Set(
                    accounts
                      .map(acc => {
                        const val = acc[mappedKey];
                        console.log(`Checking account:`, acc, `→ ${mappedKey} =`, val);
                        return val;
                      })
                      .filter(Boolean)
                  )]
                : [];
              
              return (
                <TableRow key={index} sx={{ backgroundColor: index % 2 === 0 ? "#f0f7ff" : "#e3f2fd" }}>
                  <TableCell>{row.display_order}</TableCell>
                  <TableCell>{row.line_id}</TableCell>
                  <TableCell>
                    <TextField
                      value={row.display_name}
                      onChange={(e) => handleCellChange(index, "display_name", e.target.value)}
                      variant="standard"
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      value={row.data_source}
                      onChange={(e) => handleCellChange(index, "data_source", e.target.value)}
                      variant="standard"
                    >
                      <MenuItem value="Business">Business</MenuItem>
                      <MenuItem value="Financial">Financial</MenuItem>
                      <MenuItem value="Formula">Formula</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    {row.data_source === "Business" ? (
                      <Select
                        value={activities.find(a => a.parent_cd === row.activity_cd)?.activity_cd || ""}
                        onChange={(e) => {
                          const selected = activities.find(a => a.activity_cd === e.target.value);
                          handleCellChange(index, "activity_cd", selected?.parent_cd || "");
                        }}
                        variant="standard"
                      >
                        {activities.map((a) => (
                          <MenuItem key={a.activity_cd} value={a.activity_cd}>
                            {a.activity_nm}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : "-"}
                  </TableCell>
                  <TableCell>
                    {row.data_source === "Financial" ? (
                      <Select
                        value={selectedTypeKey || ""}
                        onChange={(e) => handleAccountTypeChange(index, e.target.value)}
                        variant="standard"
                      >
                        <MenuItem value="">(Blank)</MenuItem>
                        {accountTypeOptions.map((typeKey) => (
                          <MenuItem key={typeKey} value={typeKey}>{typeKey}</MenuItem>
                        ))}
                      </Select>
                    ) : "-"}
                  </TableCell>
                  <TableCell>
                    {row.data_source === "Financial" && selectedTypeKey ? (
                      <Select
                        value={row.account_code}
                        onChange={(e) => handleCellChange(index, "account_code", e.target.value)}
                        variant="standard"
                      >
                        {accountGroupOptions.map((val, idx) => (
                          <MenuItem key={idx} value={val}>{val}</MenuItem>
                        ))}
                      </Select>
                    ) : "-"}
                  </TableCell>
                  <TableCell>
                    {row.data_source === "Formula" ? (
                      <TextField
                        value={row.formula}
                        onChange={(e) => handleCellChange(index, "formula", e.target.value)}
                        variant="standard"
                      />
                    ) : "-"}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={row.format}
                      onChange={(e) => handleCellChange(index, "format", e.target.value)}
                      variant="standard"
                    >
                      <MenuItem value="">Blank</MenuItem>
                      <MenuItem value="Currency">Currency</MenuItem>
                      <MenuItem value="Quantitative">Quantitative</MenuItem>
                      <MenuItem value="For_Calculation">For_Calculation</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Button onClick={() => handleInsertRow(index + 1)}>
                      ➕ Insert Below
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={3}>
        <Button onClick={handleAddRow} startIcon={<Add />} sx={{ mr: 2 }}>
          Add Row
        </Button>
        <Button variant="contained" color="primary" onClick={handleSave} startIcon={<Save />}>
          Save Report
        </Button>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Create New Report</DialogTitle>
        <DialogContent>
          <TextField
            label="Report Name"
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
            fullWidth
            margin="dense"
          />
          <Select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            fullWidth
            margin="dense"
            displayEmpty
            variant="outlined"
          >
            <MenuItem value="">
              <em>Select Report Type</em>
            </MenuItem>
            <MenuItem value="Financial">Financial</MenuItem>
            <MenuItem value="Business">Business</MenuItem>
            <MenuItem value="Combined">Combined</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleDialogSubmit}>Generate</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportDesign;